<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import { showApiError } from '$lib/util/errors'
  import { INCIDENT_LABELS } from '$lib/incident-labels'

  type Item =
    paths['/assistance-requests']['get']['responses'][200]['content']['application/json']['items'][number]
  type IncidentItem =
    paths['/animals/{animalId}/incidents']['get']['responses'][200]['content']['application/json']['items'][number]

  let items = $state<Item[]>([])
  let loading = $state(true)

  type ReviewDue = 'soon' | 'overdue' | 'pending'
  let reviewDue = $state<ReviewDue | ''>('')
  const REVIEW_DUE_FILTERS = [
    ['', 'All'],
    ['soon', 'Due soon'],
    ['overdue', 'Overdue'],
    ['pending', 'New docs'],
  ] as const

  const today = (): string => new Date().toISOString().slice(0, 10)

  // Small ESA review-status badge derived purely from the row fields.
  function reviewBadge(r: Item): { text: string; cls: string } | null {
    if (r.type !== 'ESA') return null
    if (r.renewalDocsPendingAt) return { text: 'New docs', cls: 'warn' }
    if (!r.reviewByDate) return null
    const t = today()
    if (r.reviewByDate < t) return { text: 'Overdue', cls: 'bad' }
    const in30 = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10)
    if (r.reviewByDate <= in30) return { text: 'Due soon', cls: 'warn' }
    return null
  }

  type View = 'table' | 'board'
  let view = $state<View>('table')

  // The board groups the eight workflow statuses into five human-readable phases.
  const PHASES = [
    { key: 'new', label: 'New', statuses: ['SUBMITTED', 'AWAITING_DOCS'] },
    { key: 'docs', label: 'Docs in', statuses: ['DOCS_RECEIVED'] },
    { key: 'review', label: 'In review', statuses: ['UNDER_REVIEW', 'NEEDS_INFO'] },
    { key: 'recommended', label: 'Recommended', statuses: ['RECOMMENDED'] },
    { key: 'decided', label: 'Decided', statuses: ['APPROVED', 'DENIED'] },
  ] as const

  const byPhase = $derived(
    Object.fromEntries(
      PHASES.map((p) => [
        p.key,
        items.filter((i) => (p.statuses as readonly string[]).includes(i.status)),
      ]),
    ) as Record<string, Item[]>,
  )

  function relAge(iso: string): string {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (s < 60) return 'just now'
    const m = Math.floor(s / 60)
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h`
    return `${Math.floor(h / 24)}d`
  }

  async function load(): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/assistance-requests', {
      params: { query: reviewDue ? { reviewDue } : {} },
    })
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = data?.items ?? []
    loading = false
  }

  async function download(requestId: number, docId: number): Promise<void> {
    const { data, error } = await api.GET('/assistance-requests/{id}/documents/{docId}/download', {
      params: { path: { id: requestId, docId } },
    })
    if (error) {
      showApiError(error)
      return
    }
    if (data?.url) window.open(data.url, '_blank', 'noopener')
  }

  let reviewing = $state<number | null>(null)
  let form = $state({
    recommendation: 'APPROVE' as 'APPROVE' | 'DENY' | 'NEEDS_INFO',
    disabilityConfirmed: false,
    needConfirmed: false,
    notes: '',
    needsInfoMessage: '',
  })

  async function claim(id: number): Promise<void> {
    const { error } = await api.POST('/assistance-requests/{id}/claim', {
      params: { path: { id } },
    })
    if (error) {
      showApiError(error)
      return
    }
    await load()
  }

  function startReview(id: number): void {
    reviewing = id
    form = {
      recommendation: 'APPROVE',
      disabilityConfirmed: false,
      needConfirmed: false,
      notes: '',
      needsInfoMessage: '',
    }
  }

  async function submitReview(id: number): Promise<void> {
    const { error } = await api.POST('/assistance-requests/{id}/review', {
      params: { path: { id } },
      body: {
        recommendation: form.recommendation,
        disabilityConfirmed: form.disabilityConfirmed,
        needConfirmed: form.needConfirmed,
        ...(form.notes ? { notes: form.notes } : {}),
        ...(form.recommendation === 'NEEDS_INFO' && form.needsInfoMessage
          ? { needsInfoMessage: form.needsInfoMessage }
          : {}),
      },
    })
    if (error) {
      showApiError(error)
      return
    }
    reviewing = null
    await load()
  }

  type Detail =
    paths['/assistance-requests/{id}']['get']['responses'][200]['content']['application/json']

  let detailId = $state<number | null>(null)
  let detail = $state<Detail | null>(null)
  let decisionNotes = $state('')
  let reviewByDate = $state('')
  let incidents = $state<IncidentItem[]>([])

  // FHA-valid grounds for a denial (HUD FHEO-2020-01). A denial must name one.
  const DENIAL_REASONS = [
    ['NO_DISABILITY', 'No disability established'],
    ['NO_DISABILITY_NEED', 'No disability-related need for the animal'],
    ['INSUFFICIENT_DOCUMENTATION', 'Requested documentation of the need not provided'],
    ['DIRECT_THREAT', 'Direct threat to health or safety not mitigable by another accommodation'],
    ['PROPERTY_DAMAGE', 'Would cause substantial physical property damage not otherwise reducible'],
    ['UNDUE_BURDEN', 'Undue financial or administrative burden'],
    ['FUNDAMENTAL_ALTERATION', 'Fundamental alteration of operations'],
  ] as const
  type DenialReason = (typeof DENIAL_REASONS)[number][0]
  let denialReason = $state<DenialReason | ''>('')
  const reasonLabel = (r: string): string => DENIAL_REASONS.find(([v]) => v === r)?.[1] ?? r

  async function openDetail(id: number): Promise<void> {
    detailId = id
    detail = null
    decisionNotes = ''
    denialReason = ''
    reviewByDate = new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10)
    const { data, error } = await api.GET('/assistance-requests/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      detailId = null // dismiss the panel so a failed fetch can't strand it on "Loading…"
      return
    }
    detail = data ?? null
    if (detail) loadVform(detail)
    // integrityByDoc is keyed by docId and only rendered for the current
    // request's documents, so it needs no reset here - and NOT clearing it means
    // a save-triggered refresh keeps a just-fetched integrity readout on screen.
    incidents = []
    if (detail?.animalId) {
      const { data: incData } = await api.GET('/animals/{animalId}/incidents', {
        params: { path: { animalId: detail.animalId } },
      })
      incidents = incData?.items ?? []
    }
  }

  async function decide(id: number, outcome: 'APPROVED' | 'DENIED'): Promise<void> {
    const { error } = await api.POST('/assistance-requests/{id}/decision', {
      params: { path: { id } },
      body: {
        outcome,
        ...(outcome === 'DENIED' && denialReason ? { denialReason } : {}),
        ...(decisionNotes ? { notes: decisionNotes } : {}),
        ...(outcome === 'APPROVED' && detail?.type === 'ESA'
          ? { reviewByDate: reviewByDate || null }
          : {}),
      },
    })
    if (error) {
      showApiError(error)
      return
    }
    detailId = null
    detail = null
    await load()
  }

  async function updateReviewBy(id: number, date: string): Promise<void> {
    const { error } = await api.PUT('/assistance-requests/{id}/review-by', {
      params: { path: { id } },
      body: { reviewByDate: date || null },
    })
    if (error) {
      showApiError(error)
      return
    }
    await openDetail(id)
  }

  let vform = $state({
    providerName: '',
    providerLicenseType: '',
    providerLicenseNumber: '',
    providerLicenseState: '',
    licenseVerified: 'NOT_CHECKED' as 'NOT_CHECKED' | 'VERIFIED' | 'NOT_FOUND' | 'UNCLEAR',
    verificationNote: '',
  })

  function loadVform(d: Detail): void {
    const v = d.verification
    vform = {
      providerName: v?.providerName ?? '',
      providerLicenseType: v?.providerLicenseType ?? '',
      providerLicenseNumber: v?.providerLicenseNumber ?? '',
      providerLicenseState: v?.providerLicenseState ?? '',
      licenseVerified: (v?.licenseVerified as typeof vform.licenseVerified) ?? 'NOT_CHECKED',
      verificationNote: v?.verificationNote ?? '',
    }
  }

  async function saveVerification(id: number): Promise<void> {
    const { error } = await api.POST('/assistance-requests/{id}/verification', {
      params: { path: { id } },
      body: {
        licenseVerified: vform.licenseVerified,
        ...(vform.providerName ? { providerName: vform.providerName } : {}),
        ...(vform.providerLicenseType ? { providerLicenseType: vform.providerLicenseType } : {}),
        ...(vform.providerLicenseNumber
          ? { providerLicenseNumber: vform.providerLicenseNumber }
          : {}),
        ...(vform.providerLicenseState ? { providerLicenseState: vform.providerLicenseState } : {}),
        ...(vform.verificationNote ? { verificationNote: vform.verificationNote } : {}),
      },
    })
    if (error) {
      showApiError(error)
      return
    }
    await openDetail(id)
  }

  type Integrity =
    paths['/assistance-requests/{id}/documents/{docId}/integrity']['get']['responses'][200]['content']['application/json']

  let integrityByDoc = $state<Record<number, Integrity>>({})

  async function checkIntegrity(id: number, docId: number): Promise<void> {
    const { data, error } = await api.GET('/assistance-requests/{id}/documents/{docId}/integrity', {
      params: { path: { id, docId } },
    })
    if (error) {
      showApiError(error)
      return
    }
    if (data) integrityByDoc = { ...integrityByDoc, [docId]: data }
  }

  onMount(load)
</script>

<h1>
  Assistance Requests
  {#if !loading}<span class="total" aria-label="{items.length} total">{items.length}</span>{/if}
</h1>

{#if loading}
  <p class="muted">Loading…</p>
{:else if items.length === 0}
  <p class="muted">No assistance requests yet.</p>
{:else}
  <div class="viewtoggle" role="group" aria-label="View">
    <button type="button" class:active={view === 'table'} onclick={() => (view = 'table')}>
      Table
    </button>
    <button type="button" class:active={view === 'board'} onclick={() => (view = 'board')}>
      Board
    </button>
  </div>
  <div class="viewtoggle reviewfilter" role="group" aria-label="Review due filter">
    {#each REVIEW_DUE_FILTERS as [value, text] (value)}
      <button
        type="button"
        class:active={reviewDue === value}
        aria-pressed={reviewDue === value}
        onclick={() => {
          reviewDue = value
          load()
        }}
      >
        {text}
      </button>
    {/each}
  </div>
  {#if view === 'table'}
    <table>
      <thead>
        <tr>
          <th>Type</th>
          <th>Status</th>
          <th>Animal</th>
          <th>Applicant</th>
          <th>Details</th>
          <th>Documents</th>
          <th>Review</th>
          <th>Decision</th>
        </tr>
      </thead>
      <tbody>
        {#each items as r (r.id)}
          <tr>
            <td>
              {r.type}
              {#if reviewBadge(r)}
                {@const b = reviewBadge(r)!}
                <span class="chip {b.cls}">{b.text}</span>
              {/if}
            </td>
            <td>{r.status}</td>
            <td>{r.animalName}</td>
            <td>
              {r.applicantName ?? '-'}
              {#if r.applicantEmail}<div class="muted">{r.applicantEmail}</div>{/if}
            </td>
            <td>
              {#if r.type === 'SERVICE'}
                <div>Required for disability: {r.requiredForDisability ? 'Yes' : 'No'}</div>
                <div>Task: {r.taskDescription}</div>
              {:else}
                <span class="muted">Documentation-based</span>
              {/if}
            </td>
            <td>
              {#if r.documents.length === 0}
                <span class="muted">None</span>
              {:else}
                {#each r.documents as d (d.id)}
                  <button class="link" onclick={() => download(r.id, d.id)}>{d.fileName}</button>
                {/each}
              {/if}
            </td>
            <td>
              {#if r.type === 'ESA' && r.status === 'DOCS_RECEIVED'}
                <button class="link" onclick={() => claim(r.id)}>Claim</button>
              {:else if r.type === 'ESA' && r.status === 'UNDER_REVIEW'}
                {#if reviewing === r.id}
                  <div class="review-form">
                    <label>
                      Recommendation
                      <select bind:value={form.recommendation}>
                        <option value="APPROVE">Recommend approve</option>
                        <option value="DENY">Recommend deny</option>
                        <option value="NEEDS_INFO">Needs more info</option>
                      </select>
                    </label>
                    <label
                      ><input type="checkbox" bind:checked={form.disabilityConfirmed} /> Disability confirmed</label
                    >
                    <label
                      ><input type="checkbox" bind:checked={form.needConfirmed} /> Disability-related
                      need confirmed</label
                    >
                    <label>
                      Notes (rationale - no medical detail)
                      <textarea bind:value={form.notes} rows="2"></textarea>
                    </label>
                    {#if form.recommendation === 'NEEDS_INFO'}
                      <label>
                        Message to applicant
                        <textarea
                          bind:value={form.needsInfoMessage}
                          rows="2"
                          placeholder="Ask only for the missing documentation of the disability-related need."
                        ></textarea>
                        <span class="guardrail"
                          >Request only missing documentation of the disability-related need. Never
                          ask for a diagnosis, medical records, or the nature of the disability.</span
                        >
                      </label>
                    {/if}
                    <div>
                      <button class="link" onclick={() => submitReview(r.id)}>Submit</button>
                      <button class="link" onclick={() => (reviewing = null)}>Cancel</button>
                    </div>
                  </div>
                {:else}
                  <button class="link" onclick={() => startReview(r.id)}>Review</button>
                {/if}
              {:else}
                <span class="muted">{r.status}</span>
              {/if}
            </td>
            <td>
              {#if r.status === 'APPROVED' || r.status === 'DENIED'}
                <span class="muted">{r.status}</span>
              {:else if (r.type === 'ESA' && r.status === 'RECOMMENDED') || (r.type === 'SERVICE' && r.status === 'SUBMITTED')}
                <button class="link" onclick={() => openDetail(r.id)}>Decide…</button>
              {:else}
                <span class="muted">-</span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="board">
      {#each PHASES as phase (phase.key)}
        {@const list = byPhase[phase.key] ?? []}
        <section class="col">
          <header class="colhead">
            <span>{phase.label}</span>
            <span class="count">{list.length}</span>
          </header>
          <div class="cards">
            {#each list as r (r.id)}
              <button type="button" class="rcard" onclick={() => openDetail(r.id)}>
                <span class="rcard-top">
                  <span class="who">{r.applicantName ?? '(no applicant)'}</span>
                  <span class="badge {r.type === 'SERVICE' ? 'svc' : 'esa'}"
                    >{r.type === 'SERVICE' ? 'Service' : 'ESA'}</span
                  >
                </span>
                <span class="animal">{r.animalName}</span>
                <span class="rcard-meta">
                  {#if r.status === 'NEEDS_INFO'}<span class="chip warn">Needs info</span>{/if}
                  {#if r.status === 'APPROVED'}<span class="chip ok">✓ Approved</span>{/if}
                  {#if r.status === 'DENIED'}<span class="chip bad">✗ Denied</span>{/if}
                  {#if r.documentCount > 0}<span class="chip doc"
                      >{r.documentCount} doc{r.documentCount === 1 ? '' : 's'}</span
                    >{/if}
                  <span class="age">{relAge(r.createdAt)}</span>
                </span>
              </button>
            {/each}
            {#if list.length === 0}<p class="colempty">-</p>{/if}
          </div>
        </section>
      {/each}
    </div>
  {/if}

  {#if detailId !== null}
    <div class="detail-panel">
      <h2>Request #{detailId}</h2>
      {#if detail === null}
        <p class="muted">Loading…</p>
      {:else}
        <p><strong>{detail.type}</strong> - {detail.status} - {detail.animalName}</p>
        {#if detail.latestReview}
          <div class="review-summary">
            <div>Recommendation: <strong>{detail.latestReview.recommendation}</strong></div>
            <div>
              Disability confirmed: {detail.latestReview.disabilityConfirmed ? 'Yes' : 'No'}
            </div>
            <div>
              Disability-related need confirmed: {detail.latestReview.needConfirmed ? 'Yes' : 'No'}
            </div>
            {#if detail.latestReview.notes}<div>Notes: {detail.latestReview.notes}</div>{/if}
          </div>
        {:else}
          <p class="muted">No reviewer recommendation (service animal or not yet reviewed).</p>
        {/if}
        <div class="incidents">
          <h3>Animal incident history</h3>
          {#if incidents.length === 0}
            <p class="muted">No incidents logged.</p>
          {:else}
            {#each incidents as i (i.id)}
              <div class="incident-row">
                <strong>{INCIDENT_LABELS[i.category] ?? i.category}</strong>
                <span class="muted"
                  >{i.occurredOn ?? 'date unknown'}{i.unitLabel ? ` - ${i.unitLabel}` : ''}</span
                >
                <div>{i.description}</div>
              </div>
            {/each}
          {/if}
        </div>
        <div class="verify">
          <h3>Provider verification <span class="muted">(advisory)</span></h3>
          <label>Provider name<input bind:value={vform.providerName} /></label>
          <label>License type<input bind:value={vform.providerLicenseType} /></label>
          <label>License #<input bind:value={vform.providerLicenseNumber} /></label>
          <label
            >License state (2-letter)<input
              maxlength="2"
              bind:value={vform.providerLicenseState}
            /></label
          >
          <label>
            Verified?
            <select bind:value={vform.licenseVerified}>
              <option value="NOT_CHECKED">Not checked</option>
              <option value="VERIFIED">Verified</option>
              <option value="NOT_FOUND">Not found</option>
              <option value="UNCLEAR">Unclear</option>
            </select>
          </label>
          <label>Note<textarea bind:value={vform.verificationNote} rows="2"></textarea></label>
          {#if detail.verification?.lookupUrl}
            <a
              class="link"
              href={detail.verification.lookupUrl}
              target="_blank"
              rel="noopener noreferrer">Look up this license →</a
            >
          {/if}
          <button class="link" onclick={() => saveVerification(detailId!)}>Save verification</button
          >
        </div>
        <div class="integrity">
          <h3>Document integrity <span class="muted">(advisory - not determinative)</span></h3>
          {#if detail.documents.length === 0}
            <p class="muted">No documents.</p>
          {:else}
            {#each detail.documents as d (d.id)}
              <div class="doc-row">
                <span>{d.fileName}</span>
                <button class="link" onclick={() => checkIntegrity(detailId!, d.id)}
                  >Check integrity</button
                >
                {#if integrityByDoc[d.id]}
                  {@const ig = integrityByDoc[d.id]!}
                  {#if ig.pdf}
                    <div class="ig">
                      <div>Producer: {ig.producer ?? '-'}</div>
                      <div>Creator: {ig.creator ?? '-'}</div>
                      <div>Created: {ig.creationDate ?? '-'}</div>
                      <div>Modified: {ig.modificationDate ?? '-'}</div>
                      <div>Pages: {ig.pageCount ?? '-'}</div>
                    </div>
                  {:else}
                    <div class="ig muted">
                      {ig.error === 'unreadable'
                        ? 'Could not read the file.'
                        : 'Not a PDF - no metadata.'}
                    </div>
                  {/if}
                {/if}
              </div>
            {/each}
          {/if}
          <p class="muted redflags">
            Reminder: online "certificates" or "registrations" for assistance animals are not, by
            themselves, reliable documentation (HUD FHEO-2020-01; FTC consumer guidance). These
            signals are advisory only - they never determine the outcome.
          </p>
        </div>
        {#if detail.decision}
          <p>
            Decision: <strong>{detail.decision.outcome}</strong>{#if detail.decision.denialReason}
              - {reasonLabel(detail.decision.denialReason)}{/if}
          </p>
          {#if detail.type === 'ESA' && detail.status === 'APPROVED'}
            <div class="reviewby">
              <h3>ESA review-by date</h3>
              {#if detail.renewalDocsPendingAt}
                <p class="renewal-note">New documentation uploaded - please re-review.</p>
              {/if}
              <p>Current review-by date: <strong>{detail.reviewByDate ?? 'none'}</strong></p>
              <label for="extend-review-by">New review-by date</label>
              <input id="extend-review-by" type="date" bind:value={reviewByDate} />
              <button class="link" onclick={() => updateReviewBy(detailId!, reviewByDate)}>
                Update review-by date
              </button>
            </div>
          {/if}
        {:else}
          <label>
            Decision notes (optional)
            <textarea bind:value={decisionNotes} rows="2"></textarea>
          </label>
          {#if detail.type === 'ESA'}
            <label for="decision-review-by">Review-by date (ESA)</label>
            <input id="decision-review-by" type="date" bind:value={reviewByDate} />
          {/if}
          <label>
            Reason for denial (required to deny)
            <select bind:value={denialReason}>
              <option value="">Select an FHA-valid reason…</option>
              {#each DENIAL_REASONS as [value, text] (value)}
                <option {value}>{text}</option>
              {/each}
            </select>
          </label>
          <div>
            <button class="link" onclick={() => decide(detailId!, 'APPROVED')}>Approve</button>
            <button
              class="link"
              onclick={() => decide(detailId!, 'DENIED')}
              disabled={!denialReason}
            >
              Deny
            </button>
            <button class="link" onclick={() => (detailId = null)}>Close</button>
          </div>
        {/if}
      {/if}
    </div>
  {/if}
{/if}

<style>
  h1 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 16px;
    font-size: 1.5rem;
    color: var(--ink-strong);
  }
  h1 .total {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--muted);
    background: var(--surface-3);
    border-radius: 999px;
    padding: 2px 12px;
  }
  .muted {
    color: var(--muted);
    font-size: 0.85rem;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: var(--surface-2);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  th,
  td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid var(--surface-3);
    font-size: 0.9rem;
    vertical-align: top;
  }
  th {
    background: var(--surface);
    font-weight: 600;
    color: var(--ink);
  }
  .link {
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    padding: 2px 0;
    display: block;
    font-size: 0.9rem;
    text-decoration: underline;
  }
  button.link:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    text-decoration: none;
  }
  .guardrail {
    display: block;
    margin-top: 4px;
    font-size: 0.78rem;
    color: var(--err-ink);
  }
  .review-form {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 220px;
  }
  .review-form label {
    font-size: 0.8rem;
    color: var(--ink);
  }
  .review-form textarea,
  .review-form select {
    width: 100%;
    font-size: 0.85rem;
  }
  .detail-panel {
    margin-top: 16px;
    padding: 12px;
    background: var(--surface-2);
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  .detail-panel h2 {
    margin: 0 0 8px;
    font-size: 1.1rem;
  }
  .review-summary {
    font-size: 0.9rem;
    margin: 8px 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .incidents {
    margin: 12px 0;
    padding: 8px 0;
    border-top: 1px solid var(--surface-3);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .incidents h3 {
    margin: 0 0 4px;
    font-size: 0.95rem;
  }
  .incident-row {
    padding: 8px;
    background: var(--surface-3);
    border-radius: 4px;
    font-size: 0.85rem;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .verify {
    margin: 12px 0;
    padding: 8px 0;
    border-top: 1px solid var(--surface-3);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .verify h3 {
    margin: 0 0 4px;
    font-size: 0.95rem;
  }
  .verify label {
    font-size: 0.8rem;
    color: var(--ink);
    display: flex;
    flex-direction: column;
  }
  .verify input,
  .verify select,
  .verify textarea {
    font-size: 0.85rem;
  }
  .integrity {
    margin: 12px 0;
    padding: 8px 0;
    border-top: 1px solid var(--surface-3);
  }
  .integrity h3 {
    margin: 0 0 4px;
    font-size: 0.95rem;
  }
  .doc-row {
    font-size: 0.85rem;
    margin: 4px 0;
  }
  .ig {
    margin: 2px 0 6px 12px;
    font-size: 0.8rem;
    color: var(--ink);
  }
  .redflags {
    margin-top: 8px;
    font-size: 0.78rem;
  }
  .reviewby {
    margin: 12px 0;
    padding: 8px 0;
    border-top: 1px solid var(--surface-3);
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.85rem;
  }
  .reviewby h3 {
    margin: 0 0 4px;
    font-size: 0.95rem;
  }
  .reviewby label {
    font-size: 0.8rem;
    color: var(--ink);
  }
  .renewal-note {
    margin: 0;
    padding: 8px 10px;
    background: color-mix(in srgb, var(--gold) 24%, var(--surface-2));
    color: var(--ink-strong);
    border-radius: 6px;
    font-weight: 600;
  }
  .reviewfilter {
    margin-left: 8px;
  }

  /* View toggle */
  .viewtoggle {
    display: inline-flex;
    gap: 2px;
    margin-bottom: 14px;
    padding: 3px;
    background: var(--surface-3);
    border-radius: 8px;
  }
  .viewtoggle button {
    border: none;
    background: transparent;
    color: var(--muted);
    padding: 6px 16px;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
  }
  .viewtoggle button.active {
    background: var(--surface-2);
    color: var(--ink-strong);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  }

  /* Kanban board */
  .board {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(190px, 1fr);
    gap: 14px;
    overflow-x: auto;
    padding-bottom: 8px;
    align-items: start;
  }
  .col {
    background: var(--surface-3);
    border-radius: 10px;
    padding: 10px;
    min-width: 0;
  }
  .colhead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--muted);
    margin: 2px 4px 10px;
  }
  .colhead .count {
    background: var(--surface-2);
    color: var(--ink);
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 0.78rem;
  }
  .cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .rcard {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    text-align: left;
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
    font: inherit;
    color: var(--ink);
    transition:
      transform 0.12s ease,
      box-shadow 0.12s ease,
      border-color 0.12s ease;
  }
  .rcard:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    border-color: color-mix(in srgb, var(--brand) 40%, var(--line));
  }
  .rcard:focus-visible {
    outline: 3px solid var(--link);
    outline-offset: 2px;
  }
  .rcard-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .rcard .who {
    font-weight: 600;
    color: var(--ink-strong);
    font-size: 0.92rem;
  }
  .rcard .animal {
    color: var(--muted);
    font-size: 0.85rem;
  }
  .badge {
    flex: none;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 2px 7px;
    border-radius: 999px;
  }
  .badge.svc {
    background: var(--brand-tint);
    color: var(--link);
  }
  .badge.esa {
    background: color-mix(in srgb, var(--gold) 20%, var(--surface-2));
    color: var(--ink);
  }
  .rcard-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }
  .chip {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--surface-3);
    color: var(--muted);
  }
  .chip.ok {
    background: var(--ok-bg);
    color: var(--ok-ink);
  }
  .chip.bad {
    background: var(--err-bg);
    color: var(--err-ink);
  }
  .chip.warn {
    background: color-mix(in srgb, var(--gold) 24%, var(--surface-2));
    color: var(--ink-strong);
  }
  .rcard .age {
    margin-left: auto;
    font-size: 0.72rem;
    color: var(--muted);
  }
  .colempty {
    margin: 4px;
    color: var(--muted);
    font-size: 0.85rem;
  }
  @media (prefers-reduced-motion: reduce) {
    .rcard:hover {
      transform: none;
    }
  }
</style>
